// ============================================================================
// Canonical Sheep Batches and Sheep Flocks route contracts
// ============================================================================
//
// SHEEP_ROUTES gives both sheep hubs a wildcard row: `/sheep/batches/*` and
// `/sheep/flocks/*`. Each router therefore owns a broad space — its bare hub
// path, a trailing slash, one record id, and every nested descendant — and the
// generic surface classifier in the resolver names the INDEX surface for the
// wildcard base while naming the DETAIL surface for any non-empty suffix.
//
// The independent release audited four exact shapes and nothing else:
//
//   /sheep/batches          SheepBatchesHub      sheep.batches.core
//   /sheep/batches/<id>     SheepBatchPage       sheep.batch.detail
//   /sheep/flocks           SheepFlocksHub       sheep.flocks.core
//   /sheep/flocks/<id>      SheepAnimalPage      sheep.animal.detail
//
// These predicates are deliberately independent of both routers so the
// resolver can classify a non-canonical raw location as LEGACY *before* ledger
// membership is consulted. If the ledger were consulted first, the generic
// RELEASED status would suppress the legacy claim while the strict render
// boundary still refused the capability, and the page would strand at the
// data-loading gate with no boot at all.
//
// RESERVED SEGMENTS. Neither prefix has a sibling route today: VIEW_TO_PATH,
// SHEEP_ROUTES, ALIASES_EXACT, and ALIASES_PREFIX contain no path nested under
// `/sheep/batches/` or `/sheep/flocks/` (unlike `/cattle/batches/forecast` or
// the `/fleet/*` entry namespaces). The reserved lists below are therefore
// empty ON PURPOSE rather than by omission, and the release's static proof
// re-derives them from the live route tables so that adding one without
// reserving it here fails loudly instead of rendering a record page for a
// redirect target.
// ============================================================================

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';

export const SHEEP_BATCHES_INDEX_PATHNAME = '/sheep/batches';
export const SHEEP_BATCHES_DETAIL_PATHNAME_PREFIX = '/sheep/batches/';
export const SHEEP_FLOCKS_INDEX_PATHNAME = '/sheep/flocks';
export const SHEEP_FLOCKS_DETAIL_PATHNAME_PREFIX = '/sheep/flocks/';

/** See the RESERVED SEGMENTS note above: empty is a derived fact, not a gap. */
export const SHEEP_BATCHES_DETAIL_RESERVED_IDS: readonly string[] = Object.freeze([]);
export const SHEEP_FLOCKS_DETAIL_RESERVED_IDS: readonly string[] = Object.freeze([]);

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

// A raw record id may not carry a separator, a NUL, a line break, or the two
// characters that would mean this "pathname" is really a full URL fragment.
// The same set is re-tested after decoding so one percent-escape cannot widen
// the capability past what was reviewed.
function hasUnsafeCharacter(value: string): boolean {
  return (
    value.includes('/') ||
    value.includes('\\') ||
    value.includes('\0') ||
    value.includes('\r') ||
    value.includes('\n') ||
    value.includes('?') ||
    value.includes('#')
  );
}

/**
 * Accept exactly one meaningful, unreserved, decoded-safe record-id segment
 * under `prefix`. Decoding happens ONCE and every rule is re-applied to the
 * decoded value, so `%2F`, `%5C`, `%00`, `%0A`, `%0D`, `%2E`, and `%2E%2E`
 * are rejected for the same reasons their literal forms are.
 */
function isCanonicalRecordPathname(pathname: unknown, prefix: string, reserved: readonly string[]): boolean {
  if (typeof pathname !== 'string' || !pathname.startsWith(prefix)) return false;

  const rawId = pathname.slice(prefix.length);
  if (rawId === '' || hasUnsafeCharacter(rawId)) return false;

  try {
    const id = decodeURIComponent(rawId);
    return id.trim() !== '' && id !== '.' && id !== '..' && !hasUnsafeCharacter(id) && !reserved.includes(id);
  } catch {
    // A malformed escape such as a bare `%` is not a record id.
    return false;
  }
}

/**
 * React Router supplies pathname/search/hash separately. A bare string is
 * accepted only when it is plainly a pathname, and a location object must
 * carry neither: both record pages read `location.hash` as a scroll anchor and
 * that presentation behaviour has not been audited on the independent path,
 * so a query or fragment keeps the record on the retained legacy router.
 */
function isCanonicalRecordLocation(location: unknown, prefix: string, reserved: readonly string[]): boolean {
  if (typeof location === 'string') return isCanonicalRecordPathname(location, prefix, reserved);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    isCanonicalRecordPathname(pathname, prefix, reserved)
  );
}

// Only `pathname` participates in a hub claim. Neither hub reads search or
// hash, and React Router already carries them separately, so an exact literal
// comparison is both the whole rule and its own malformed-input guard.
function pathnameOf(location: unknown): string | null {
  if (typeof location === 'string') return location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) return null;
  const pathname = (location as LocationLike).pathname;
  return typeof pathname === 'string' ? pathname : null;
}

export function isCanonicalSheepBatchesDetailPathname(pathname: unknown): pathname is string {
  return isCanonicalRecordPathname(pathname, SHEEP_BATCHES_DETAIL_PATHNAME_PREFIX, SHEEP_BATCHES_DETAIL_RESERVED_IDS);
}

export function isCanonicalSheepBatchesDetailLocation(location: unknown): boolean {
  return isCanonicalRecordLocation(location, SHEEP_BATCHES_DETAIL_PATHNAME_PREFIX, SHEEP_BATCHES_DETAIL_RESERVED_IDS);
}

export function isCanonicalSheepFlocksDetailPathname(pathname: unknown): pathname is string {
  return isCanonicalRecordPathname(pathname, SHEEP_FLOCKS_DETAIL_PATHNAME_PREFIX, SHEEP_FLOCKS_DETAIL_RESERVED_IDS);
}

export function isCanonicalSheepFlocksDetailLocation(location: unknown): boolean {
  return isCanonicalRecordLocation(location, SHEEP_FLOCKS_DETAIL_PATHNAME_PREFIX, SHEEP_FLOCKS_DETAIL_RESERVED_IDS);
}

/**
 * The wildcard base `/sheep/batches/` classifies as the INDEX surface, exactly
 * the shape that let a router base suppress its own legacy fallback in the
 * Layer Batches release. Only the bare canonical hub path is released.
 */
export function isCanonicalSheepBatchesIndexPathname(pathname: unknown): pathname is string {
  return pathname === SHEEP_BATCHES_INDEX_PATHNAME;
}

export function isCanonicalSheepBatchesIndexLocation(location: unknown): boolean {
  return pathnameOf(location) === SHEEP_BATCHES_INDEX_PATHNAME;
}

export function isCanonicalSheepFlocksIndexPathname(pathname: unknown): pathname is string {
  return pathname === SHEEP_FLOCKS_INDEX_PATHNAME;
}

export function isCanonicalSheepFlocksIndexLocation(location: unknown): boolean {
  return pathnameOf(location) === SHEEP_FLOCKS_INDEX_PATHNAME;
}
